import { useEffect, useState } from "react";
import { CheckCircle2, Circle } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SaveIndicator } from "@/components/shared/save-indicator";
import { useCheckInKeyResult } from "@/hooks/use-key-results";
import { useAutoSave } from "@/hooks/use-auto-save";
import { getFiscalLabel } from "@/lib/fiscal-year";
import { formatTimestamp } from "@/lib/date";
import {
  buildKrPaceContext,
  computePaceResult,
  getProgressLabel,
  getProgressStatus,
  PACE_GAP,
  type ProgressStatus,
} from "@/lib/progress-status";
import { cn } from "@/lib/utils";
import type { KeyResult } from "@/types/entities";
import type { components } from "@/types/api.generated";

interface KrSliderRowProps {
  kr: KeyResult;
  canEdit: boolean;
  year?: number;
}

const STATUS_BG: Record<ProgressStatus, string> = {
  on_track: "bg-[hsl(var(--info))]",
  on_pace: "bg-[hsl(var(--primary))]",
  at_risk: "bg-[hsl(var(--accent))]",
  off_track: "bg-[hsl(var(--destructive))]",
};

const PACE_LEGEND: Array<{ status: ProgressStatus; label: string; range: string }> = [
  { status: "on_track", label: "On track", range: `Ahead by > ${PACE_GAP.AHEAD} pts, or complete` },
  { status: "on_pace", label: "On pace", range: `Within ±${PACE_GAP.AHEAD} pts of pace` },
  { status: "at_risk", label: "At risk", range: `${PACE_GAP.AT_RISK}–${PACE_GAP.OFF_TRACK} pts behind pace` },
  { status: "off_track", label: "Off track", range: `> ${PACE_GAP.OFF_TRACK} pts behind, or overdue` },
];

function hasValidMetricTarget(
  kr: KeyResult,
): kr is KeyResult & { target_value: number } {
  return (
    kr.type === "metric" &&
    typeof kr.target_value === "number" &&
    kr.target_value > 0
  );
}

function metricStep(targetValue: number): number {
  return Number.isInteger(targetValue) ? 1 : Math.max(targetValue / 100, 0.01);
}

function metricPercent(current: number, target: number): number {
  if (target <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((current / target) * 100)));
}

function initialLocalValue(kr: KeyResult): number {
  if (kr.type === "metric") {
    return Math.max(0, kr.current_value ?? 0);
  }
  return Math.round(kr.completion_percent ?? 0);
}

function formatHelper(
  kr: KeyResult,
  localValue: number,
  displayPercent: number,
): string {
  if (kr.type === "metric") {
    if (!hasValidMetricTarget(kr)) {
      const current = kr.current_value;
      return `Target: — · Current: ${current ?? "—"}`;
    }
    return `Target: ${kr.target_value} · Current: ${localValue}`;
  }
  if (kr.type === "binary") {
    return displayPercent >= 100 ? "Done" : "Not done";
  }
  return `${displayPercent}% of 100%`;
}

export function KrSliderRow({ kr, canEdit, year }: KrSliderRowProps) {
  const mutation = useCheckInKeyResult(kr.id, { silent: true });
  const { saveStatus, debouncedSave, save } = useAutoSave<
    components["schemas"]["CheckInRequest"]
  >({
    mutation,
    delay: 1000,
  });

  const [localValue, setLocalValue] = useState<number>(initialLocalValue(kr));

  useEffect(() => {
    if (saveStatus !== "saving") {
      setLocalValue(initialLocalValue(kr));
    }
  }, [
    kr.id,
    kr.type,
    kr.current_value,
    kr.completion_percent,
    saveStatus,
  ]);

  const displayPercent =
    kr.type === "metric"
      ? hasValidMetricTarget(kr)
        ? metricPercent(localValue, kr.target_value)
        : Math.round(kr.completion_percent ?? 0)
      : localValue;
  const paceContext = buildKrPaceContext(kr, year);
  const paceResult = computePaceResult(displayPercent, paceContext);
  const status = paceResult ? paceResult.status : getProgressStatus(displayPercent);
  const fiscalLabel = year ? getFiscalLabel(kr.quarter, year) : "";
  const isBinaryDone = kr.type === "binary" && displayPercent >= 100;

  const handlePercentChange = (next: number) => {
    if (!canEdit) return;
    const clamped = Math.max(0, Math.min(100, Math.round(next)));
    setLocalValue(clamped);
    debouncedSave({ completion_percent: clamped });
  };

  const handleMetricChange = (next: number) => {
    if (!canEdit || !hasValidMetricTarget(kr)) return;
    const target = kr.target_value;
    const step = metricStep(target);
    const snapped = Math.round(next / step) * step;
    const clamped = Math.max(0, Math.min(target, snapped));
    setLocalValue(clamped);
    debouncedSave({ current_value: clamped });
  };

  const handleBinaryToggle = () => {
    if (!canEdit) return;
    const next = isBinaryDone ? 0 : 100;
    setLocalValue(next);
    save({ completion_percent: next });
  };

  return (
    <div className="grid grid-cols-[80px_1fr_220px_120px] items-center gap-4 rounded-md px-2 py-2 text-sm hover:bg-[hsl(var(--muted))]">
      <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
        {kr.quarter}
        {fiscalLabel ? ` (${fiscalLabel})` : ""}
      </span>
      <div className="min-w-0">
        <p className="truncate font-medium text-[hsl(var(--foreground))]">{kr.title}</p>
        <p className="truncate text-xs text-[hsl(var(--muted-foreground))] tabular-nums">
          {formatHelper(kr, localValue, displayPercent)}
        </p>
        <p className="truncate text-[11px] text-[hsl(var(--muted-foreground))]">
          Updated {formatTimestamp(kr.updated_at)}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {kr.type === "binary" ? (
          <Button
            type="button"
            variant={isBinaryDone ? "default" : "outline"}
            size="sm"
            disabled={!canEdit}
            onClick={handleBinaryToggle}
            className="w-full"
            aria-label={
              isBinaryDone
                ? `Mark "${kr.title}" not complete`
                : `Mark "${kr.title}" complete`
            }
            aria-pressed={isBinaryDone}
          >
            {isBinaryDone ? (
              <>
                <CheckCircle2 aria-hidden="true" /> Complete
              </>
            ) : (
              <>
                <Circle aria-hidden="true" /> Mark complete
              </>
            )}
          </Button>
        ) : kr.type === "metric" ? (
          hasValidMetricTarget(kr) ? (
            <>
              <Slider
                aria-label={`Progress for ${kr.title}`}
                value={localValue}
                min={0}
                max={kr.target_value}
                step={metricStep(kr.target_value)}
                disabled={!canEdit}
                onValueChange={handleMetricChange}
                rangeClassName={STATUS_BG[status]}
              />
              <span className="w-10 shrink-0 text-right font-mono text-xs font-bold tabular-nums">
                {displayPercent}%
              </span>
            </>
          ) : (
            <span className="text-xs italic text-[hsl(var(--muted-foreground))]">
              Set a target to enable progress
            </span>
          )
        ) : (
          <>
            <Slider
              aria-label={`Progress for ${kr.title}`}
              value={localValue}
              min={0}
              max={100}
              step={1}
              disabled={!canEdit}
              onValueChange={handlePercentChange}
              rangeClassName={STATUS_BG[status]}
            />
            <span className="w-10 shrink-0 text-right font-mono text-xs font-bold tabular-nums">
              {displayPercent}%
            </span>
          </>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className={cn(
                "inline-flex cursor-help items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                "border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]",
              )}
              aria-label={`Status ${getProgressLabel(displayPercent, paceContext)} at ${displayPercent} percent`}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", STATUS_BG[status])} />
              {getProgressLabel(displayPercent, paceContext)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs space-y-2 p-3">
            <p className="text-xs font-medium">
              {paceResult
                ? `${getProgressLabel(displayPercent, paceContext)} at ${displayPercent}% — expected pace today is ${Math.round(paceResult.expectedPercent)}%.`
                : `${getProgressLabel(displayPercent)} at ${displayPercent}% complete.`}
            </p>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              {paceResult
                ? "Status compares your progress to a linear pace from start through due date."
                : "Status is based on completion only — set a quarter or due date to enable pace tracking."}
            </p>
            <ul className="space-y-1 text-xs">
              {PACE_LEGEND.map((item) => (
                <li
                  key={item.status}
                  className={cn(
                    "flex items-center gap-2",
                    item.status === status
                      ? "font-semibold text-[hsl(var(--foreground))]"
                      : "text-[hsl(var(--muted-foreground))]",
                  )}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", STATUS_BG[item.status])} />
                  <span className="flex-1">{item.label}</span>
                  <span className="tabular-nums text-right">{item.range}</span>
                </li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
        <SaveIndicator status={saveStatus} />
      </div>
    </div>
  );
}
